import { useState } from 'react';
import styled from '@emotion/styled/macro';
import { UserProfile } from '@sunrun/sales-experience-shared';
import {
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
  useSingletonTabletMediaQuery,
  Banner,
  BannerTypes,
  Button,
  Modal,
} from '@sunrun/experience-ui-components';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import AppleAppStore from '../../assets/images/AppleAppStore.svg';
import GooglePlayStore from '../../assets/images/GooglePlayStore.svg';
import { UrgentTasks } from 'components/organisms/UrgentTasks';
import { AskHelpWidget } from 'components/organisms/AskHelpWidget';
import { SalesSection } from 'components/organisms/SalesSection';
import { ResourcesSection } from 'components/organisms/ResourcesSection';
import { LearningSection } from 'components/organisms/LearningSection';
import { Mixpanel } from 'providers/Mixpanel';
import { DASHBOARD_OVERVIEW__LANDED_ON_PAGE } from 'constants/mixpanelEvents';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';

const DashboardOverview = ({
  isSRM,
  userProfile,
  warnings,
}: {
  isSRM: boolean;
  userProfile: UserProfile;
  warnings?: React.ReactNode;
}) => {
  Mixpanel.track(DASHBOARD_OVERVIEW__LANDED_ON_PAGE, {
    contactId: userProfile.contactId,
  });
  const [viewModal, setViewModal] = useState(false);
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();
  const showMobileDownloadSection = isSRM;

  const isBlockFusionRep =
    !!userProfile.isFusion || userProfile.partnerName === 'Fusion';
  const showTrackSalesSection = !isBlockFusionRep;

  // Default to true if the flag is undefined
  const showBanner =
    useFeatureFlagEnabled(FEATURE_FLAGS.PAYMENT_OUTAGE) ?? true;

  return (
    <>
      <DashboardOverviewContainer data-testid="new-dashboard-overview-container">
        <Dashboard>
          {warnings}
          <Typography
            variant="h1"
            size={isMobile ? 30 : 44}
            fontWeight="medium">
            Dashboard Overview
          </Typography>

          {showBanner && (
            <Banner
              title="Scheduled payment system maintenance: payment & billing currently unavailable."
              bannerType={BannerTypes.InfoLight}
              show={true}
              showClose={false}
              showIcon={true}
              customButtons={
                <Button
                  color="link"
                  size="sm"
                  style={{
                    color: tokens.INFO_20,
                    textDecorationColor: tokens.INFO_20,
                  }}
                  onClick={() => {
                    setViewModal(true);
                  }}>
                  Learn More
                </Button>
              }
            />
          )}
          <DashboardGrid isMobile={isMobile}>
            <TasksSection isMobile={isMobile}>
              <UrgentTasks userProfile={userProfile} />
            </TasksSection>

            <CurrentSectionSection isMobile={isMobile}>
              <AskHelpWidget />
            </CurrentSectionSection>

            {showTrackSalesSection && (
              <SalesSectionSection isMobile={isMobile}>
                <SalesSection />
              </SalesSectionSection>
            )}

            <ResourcesSectionSection isMobile={isMobile}>
              <ResourcesSection userProfile={userProfile} />
            </ResourcesSectionSection>

            {showMobileDownloadSection && (
              <MobileDownloadStyledContainer isTablet={isTablet}>
                <MobileDownloadSectionSection
                  data-testid="mobile-download-section"
                  isMobile={isMobile}
                  isTablet={isTablet}>
                  <MobileDownloadSection
                    isTablet={isTablet}
                    isMobile={isMobile}>
                    <Typography
                      variant="h2"
                      size={isMobile || isTablet ? 16 : 14}
                      fontWeight="medium"
                      color={tokens.WHITE}>
                      Download ONE mobile app
                    </Typography>
                    <MobileStoreLinksContainer>
                      <MobileStoreLink
                        data-testid="apple-app-store-link"
                        isTablet={isTablet}
                        isMobile={isMobile}
                        target="_blank"
                        rel="noreferrer noopener"
                        href="https://apps.apple.com/us/app/one-sunrun-sales-app/id6749865739"
                        aria-label="Apple App store">
                        <img src={AppleAppStore} alt={'Apple App Store logo'} />
                      </MobileStoreLink>
                      <MobileStoreLink
                        data-testid="google-play-store-link"
                        isTablet={isTablet}
                        isMobile={isMobile}
                        target="_blank"
                        rel="noreferrer noopener"
                        href="https://play.google.com/store/apps/details?id=com.sunrun.sunrunone"
                        aria-label="Google Play store">
                        <img
                          src={GooglePlayStore}
                          alt={'Google Play store logo'}
                        />
                      </MobileStoreLink>
                    </MobileStoreLinksContainer>
                  </MobileDownloadSection>
                </MobileDownloadSectionSection>
                <LearningSectionSection
                  isMobile={isMobile}
                  showMobileDownloadSection={showMobileDownloadSection}>
                  <LearningSection />
                </LearningSectionSection>
              </MobileDownloadStyledContainer>
            )}

            {!showMobileDownloadSection && (
              <LearningSectionSection
                isMobile={isMobile}
                showMobileDownloadSection={showMobileDownloadSection}>
                <LearningSection />
              </LearningSectionSection>
            )}
          </DashboardGrid>
        </Dashboard>
      </DashboardOverviewContainer>
      {viewModal && (
        <Modal
          testId="scheduled-billing-maintenance-modal"
          onClose={() => setViewModal(false)}
          title="Scheduled Billing Maintenance"
          primaryButton={{
            text: 'Got it',
            onClick: () => setViewModal(false),
          }}>
          <div>
            <Typography>
              As part of scheduled maintenance, billing (including autopay
              changes) is currently unavailable. This includes access via the
              app, http://my.sunrun.com, or through our phone support.
            </Typography>
            <Typography>Thank you for your patience!</Typography>
          </div>
        </Modal>
      )}
    </>
  );
};

const DashboardOverviewContainer = styled.div`
  scrollbar-gutter: 16px;
  @media (max-width: 880px) {
    padding: 0;
  }
  min-height: 200px;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Dashboard = styled.div`
  width: 100%;
  max-width: 1400px;
`;

const DashboardGrid = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto auto;
  gap: ${({ isMobile }) => (isMobile ? '16px' : '32px')};
  margin-top: ${({ isMobile }) => (isMobile ? '12px' : '32px')};
  width: 100%;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto auto auto auto;
  }
`;

// Common base component for all sections
const SectionContainer = styled.div<{ isMobile: boolean }>`
  background: transparent;
  border-radius: 8px;
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  padding: 1.5rem;

  ${({ isMobile }) =>
    isMobile &&
    `
    padding: 16px;
  `}
`;

const TasksSection = styled(SectionContainer)`
  grid-column: 1 / span 2;
  grid-row: 1 / span 2;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  padding: 0;
  border: none;
  overflow: hidden;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: 1 / span 2;
    grid-row: 1;
  }
`;

const CurrentSectionSection = styled(SectionContainer)<{ isMobile: boolean }>`
  grid-column: 3;
  grid-row: 1 / span 3;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: 1;
    grid-row: 3 / span 2;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-column: 1 / span 2;
    grid-row: 4 / span 2;
  }
`;

const SalesSectionSection = styled(SectionContainer)<{ isMobile: boolean }>`
  grid-column: 1 / span 2;
  grid-row: 3;
  display: flex;
  flex-direction: column;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: 1 / span 2;
    grid-row: 2;
  }
`;

const ResourcesSectionSection = styled(SectionContainer)<{ isMobile: boolean }>`
  grid-column: 1 / span 2;
  grid-row: 4;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: 2;
    grid-row: 4;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-column: 1 / span 2;
    grid-row: 7;
  }
`;

const MobileStoreLinksContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 16px;
`;

const MobileStoreLink = styled.a<{ isTablet: boolean; isMobile: boolean }>`
  display: flex;
  align-items: center;

  > img {
    width: ${({ isTablet, isMobile }) =>
      isTablet || isMobile ? '146px' : '112px'};
  }
`;

const MobileDownloadStyledContainer = styled.div<{ isTablet: boolean }>`
  display: flex;
  flex-direction: ${({ isTablet }) => (isTablet ? 'column-reverse' : 'column')};
  justify-content: space-between;
  gap: 16px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: 2;
    grid-row: 3;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-column: 1 / span 2;
    grid-row: 6;
  }
`;

const MobileDownloadSection = styled.div<{
  isTablet: boolean;
  isMobile: boolean;
}>`
  display: flex;
  flex-direction: ${({ isTablet, isMobile }) =>
    isTablet || isMobile ? 'column' : 'row'};
  justify-content: space-between;
  gap: 16px;

  > h2 {
    max-width: ${({ isTablet, isMobile }) =>
      isTablet || isMobile ? 'unset' : '112px'};
  }
`;

const MobileDownloadSectionSection = styled(SectionContainer)<{
  isTablet: boolean;
  isMobile: boolean;
}>`
  padding: ${({ isTablet, isMobile }) =>
    isTablet || isMobile ? '1.5rem' : '16px'};
  grid-column: 3;
  grid-row: 4;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #1f2647 0%, #000 100%);
`;

const LearningSectionSection = styled(SectionContainer)<{
  showMobileDownloadSection: boolean;
}>`
  grid-column: 3;
  grid-row: ${({ showMobileDownloadSection }) =>
    showMobileDownloadSection ? '5' : '4'};
  display: flex;
  flex-direction: column;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: 2;
    grid-row: 3;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-column: 1 / span 2;
    grid-row: 6;
  }
`;

export { DashboardOverview };
